/**
 * The tail's drive in three — ADR-038 R5 (ruled 2026-09-24): **the mood's
 * angles as per-bone local quaternions, over axes measured at bind.**
 *
 * ONE responsibility: turn the kernel's engine-free drive (`moodAngles`, the
 * cross-fade, the declared axes) into the per-bone local offsets the spring
 * runtime post-multiplies onto the animated pose — or that the viewport applies
 * kinematically when the spring is off (the drive with no physics).
 *
 * THE AXES. Per chain bone, in the bone's LOCAL bind frame: `yaw` = the body's
 * up (world +Y at bind), `pitch` = the axis that turns the tail's horizontal
 * bind direction toward the ground (`dirH × down`, so a positive pitch is
 * DOWN). Measured on the skeleton put on its bind (every bone's TRS restored
 * after, bit-for-bit) and DECLARED in the file (`drive.axes`), so a reader
 * never guesses a body's up from a pelvis bone's arbitrary frame.
 *
 * Depends on: three, kernel (moods). Depended on by: the app's spring, the
 * drive probe.
 */
import { Quaternion, Vector3, type Object3D, type SkinnedMesh } from 'three';
import {
  moodAngles,
  moodFade,
  moodGains,
  TAIL_DYNAMICS_DEFAULTS,
  TAIL_FLAT_ASPECT,
  TAIL_HINGE_DEFAULTS,
  type SpringDynamics,
  type TailHinge,
  type TailMood,
  type WagKnobs,
} from '@harryhausen/kernel';
import type { SpringRuntimeDrive } from './spring-runtime.js';

export type Vec3 = [number, number, number];
export interface DriveAxes {
  readonly yaw: Vec3;
  readonly pitch: Vec3;
}

const UP = new Vector3(0, 1, 0);
const DOWN = new Vector3(0, -1, 0);

function skinOf(root: Object3D, bone: Object3D): SkinnedMesh | null {
  let found: SkinnedMesh | null = null;
  root.traverse((o) => {
    const m = o as SkinnedMesh;
    if (found === null && m.isSkinnedMesh === true && m.skeleton.bones.includes(bone as never)) found = m;
  });
  return found;
}

/**
 * The drive axes of a chain (root → tip), measured at BIND. The scene is put
 * on its bind through the skin that binds the chain's root and every node's
 * TRS restored after; a chain no skin binds is measured where it stands.
 */
export function measureDriveAxes(root: Object3D, bones: readonly Object3D[]): DriveAxes[] {
  const skin = bones[0] === undefined ? null : skinOf(root, bones[0]);
  const saved: { o: Object3D; p: Vector3; q: Quaternion; s: Vector3 }[] = [];
  if (skin !== null) root.traverse((o) => saved.push({ o, p: o.position.clone(), q: o.quaternion.clone(), s: o.scale.clone() }));
  try {
    skin?.skeleton.pose();
    root.updateMatrixWorld(true);
    const first = bones[0]!.getWorldPosition(new Vector3());
    const last = bones[bones.length - 1]!.getWorldPosition(new Vector3());
    const dir = last.sub(first);
    dir.y = 0;
    // A tail that hangs straight down has no horizontal direction: fall back to the body's back (−Z, the +Z-facing convention).
    if (dir.lengthSq() < 1e-8) dir.set(0, 0, -1);
    dir.normalize();
    const pitchWorld = new Vector3().crossVectors(dir, DOWN).normalize();
    return bones.map((b) => {
      const inv = b.getWorldQuaternion(new Quaternion()).invert();
      const yaw = UP.clone().applyQuaternion(inv).normalize();
      const pitch = pitchWorld.clone().applyQuaternion(inv).normalize();
      return { yaw: yaw.toArray() as Vec3, pitch: pitch.toArray() as Vec3 };
    });
  } finally {
    for (const { o, p, q, s } of saved) {
      o.position.copy(p);
      o.quaternion.copy(q);
      o.scale.copy(s);
    }
    if (skin !== null) root.updateMatrixWorld(true);
  }
}

/** Eigen-decomposition of a symmetric 3×3 (cyclic Jacobi): values descending, unit vectors. */
function symmetricEigen3(m: number[][]): { values: number[]; vectors: Vector3[] } {
  const a = m.map((r) => [...r]);
  const v = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  for (let sweep = 0; sweep < 50; sweep++) {
    let off = 0;
    for (let p = 0; p < 3; p++) for (let q = p + 1; q < 3; q++) off += a[p]![q]! ** 2;
    if (off < 1e-30) break;
    for (let p = 0; p < 3; p++) {
      for (let q = p + 1; q < 3; q++) {
        if (Math.abs(a[p]![q]!) < 1e-300) continue;
        const theta = (a[q]![q]! - a[p]![p]!) / (2 * a[p]![q]!);
        const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
        const c = 1 / Math.sqrt(t * t + 1);
        const sn = t * c;
        for (let k = 0; k < 3; k++) {
          const akp = a[k]![p]!, akq = a[k]![q]!;
          a[k]![p] = c * akp - sn * akq;
          a[k]![q] = sn * akp + c * akq;
        }
        for (let k = 0; k < 3; k++) {
          const apk = a[p]![k]!, aqk = a[q]![k]!;
          a[p]![k] = c * apk - sn * aqk;
          a[q]![k] = sn * apk + c * aqk;
        }
        for (let k = 0; k < 3; k++) {
          const vkp = v[k]![p]!, vkq = v[k]![q]!;
          v[k]![p] = c * vkp - sn * vkq;
          v[k]![q] = sn * vkp + c * vkq;
        }
      }
    }
  }
  const order = [0, 1, 2].sort((i, j) => a[j]![j]! - a[i]![i]!);
  return { values: order.map((i) => a[i]![i]!), vectors: order.map((i) => new Vector3(v[0]![i]!, v[1]![i]!, v[2]![i]!).normalize()) };
}

export interface TailFlatness {
  /** Per sprung joint: cross-section aspect (width / thickness) of the flesh the joint's bone dominates. */
  readonly aspect: number[];
  /** Per sprung joint, bone-local bind frame: the flesh's WIDTH direction — the hinge a paddle bends about. */
  readonly hingeLocal: Vec3[];
  /** Per sprung joint, world at bind: the thin direction (pointing up). */
  readonly thinWorld: Vec3[];
  readonly medianAspect: number;
}

/**
 * How flat a tail's flesh is, segment by segment (ADR-038 §13.7), at BIND:
 * the vertices each sprung joint's bone dominates (largest skin weight),
 * their offsets from the bone's segment line, and the 2-D spread of those
 * offsets across it — width over thickness is the aspect, the thin direction
 * the paddle's normal, the width direction the hinge. Null when no skin binds
 * the chain or a segment owns too few vertices to say.
 */
export function measureTailFlatness(root: Object3D, bones: readonly Object3D[]): TailFlatness | null {
  const skin = bones[0] === undefined ? null : skinOf(root, bones[0]);
  if (skin === null) return null;
  const saved: { o: Object3D; p: Vector3; q: Quaternion; s: Vector3 }[] = [];
  root.traverse((o) => saved.push({ o, p: o.position.clone(), q: o.quaternion.clone(), s: o.scale.clone() }));
  try {
    skin.skeleton.pose();
    root.updateMatrixWorld(true);
    const index = new Map<Object3D, number>(bones.slice(0, -1).map((b, k) => [b, k]));
    const skelIndex = skin.skeleton.bones.map((b) => index.get(b) ?? -1);
    const pos = skin.geometry.getAttribute('position');
    const si = skin.geometry.getAttribute('skinIndex');
    const sw = skin.geometry.getAttribute('skinWeight');
    const heads = bones.map((b) => b.getWorldPosition(new Vector3()));
    const sums = heads.slice(0, -1).map(() => ({ n: 0, c: [[0, 0, 0], [0, 0, 0], [0, 0, 0]] as number[][] }));
    const v = new Vector3();
    for (let i = 0; i < pos.count; i++) {
      let best = -1;
      let bestW = 0;
      for (let c = 0; c < 4; c++) {
        const w = sw.getComponent(i, c);
        if (w > bestW) {
          bestW = w;
          best = si.getComponent(i, c);
        }
      }
      const k = best >= 0 ? skelIndex[best]! : -1;
      if (k < 0) continue;
      v.fromBufferAttribute(pos, i).applyMatrix4(skin.matrixWorld);
      const a = heads[k]!;
      const dir = heads[k + 1]!.clone().sub(a).normalize();
      const off = v.clone().sub(a);
      off.addScaledVector(dir, -off.dot(dir));
      const o = [off.x, off.y, off.z];
      const acc = sums[k]!;
      acc.n += 1;
      for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) acc.c[r]![c]! += o[r]! * o[c]!;
    }
    if (sums.some((x) => x.n < 6)) return null;
    const aspect: number[] = [];
    const hingeLocal: Vec3[] = [];
    const thinWorld: Vec3[] = [];
    sums.forEach((acc, k) => {
      const { values, vectors } = symmetricEigen3(acc.c.map((r) => r.map((x) => x / acc.n)));
      // The segment's own direction carries ~no spread (offsets were projected off it): the two largest are width and thickness.
      const width = vectors[0]!;
      const thin = vectors[1]!.clone();
      if (thin.y < 0) thin.negate();
      aspect.push(Math.sqrt(values[0]! / Math.max(values[1]!, 1e-12)));
      const inv = bones[k]!.getWorldQuaternion(new Quaternion()).invert();
      hingeLocal.push(width.clone().applyQuaternion(inv).normalize().toArray() as Vec3);
      thinWorld.push(thin.toArray() as Vec3);
    });
    const sorted = [...aspect].sort((x, y) => x - y);
    return { aspect, hingeLocal, thinWorld, medianAspect: sorted[Math.floor(sorted.length / 2)]! };
  } finally {
    for (const { o, p, q, s: sc } of saved) {
      o.position.copy(p);
      o.quaternion.copy(q);
      o.scale.copy(sc);
    }
    root.updateMatrixWorld(true);
  }
}

/** The hinge a flat tail declares (§13.7), or null for a round one. */
export function tailHingeFromFlatness(f: TailFlatness | null): TailHinge | null {
  if (f === null || f.medianAspect < TAIL_FLAT_ASPECT) return null;
  return { axes: f.hingeLocal, ...TAIL_HINGE_DEFAULTS, aspect: f.aspect.map((a) => +a.toFixed(4)) };
}

export interface TailDriveSettings {
  readonly mood: TailMood;
  readonly muscle: number;
  readonly wag: WagKnobs;
  readonly reflex: boolean;
  readonly inertia: number;
  /** Measurement only: override declared dynamics (the probe's sweeps). */
  readonly dynamics?: Partial<SpringDynamics>;
}

/**
 * The drive a spring runtime layers over (and the kinematic drive when the
 * spring is off). Mood changes cross-fade the angles AND the gains over
 * `TAIL_MOOD_FADE_SECONDS`, in the clock `sample` is asked in (the runtime's
 * step time); a muscle or wag change applies at once (a slider is continuous).
 */
export interface TailDrive extends SpringRuntimeDrive {
  readonly settings: TailDriveSettings;
  /** New settings at time `seconds` (the clock `sample` is asked in). */
  update(settings: TailDriveSettings, seconds: number): void;
  /** The offsets alone (the drive without physics, spring off). */
  offsets(seconds: number, out: Quaternion[]): void;
}

export function createTailDrive(axes: readonly DriveAxes[], initial: TailDriveSettings, hinge: TailHinge | null = null): TailDrive {
  const n = axes.length;
  const yawAxes = axes.map((a) => new Vector3(...a.yaw));
  const pitchAxes = axes.map((a) => new Vector3(...a.pitch));
  let settings = initial;
  let from: TailMood = initial.mood;
  let since = -Infinity;
  let dynamics: SpringDynamics = { ...TAIL_DYNAMICS_DEFAULTS, ...initial.dynamics, inertia: initial.inertia };
  const qy = new Quaternion();
  const qp = new Quaternion();
  const offsets = (seconds: number, out: Quaternion[]): void => {
    const f = moodFade(seconds - since);
    const b = moodAngles(settings.mood, seconds, n, settings.wag);
    const a = f < 1 ? moodAngles(from, seconds, n, settings.wag) : b;
    for (let k = 0; k < n && k < out.length; k++) {
      const yaw = a.yaw[k]! + (b.yaw[k]! - a.yaw[k]!) * f;
      const pitch = a.pitch[k]! + (b.pitch[k]! - a.pitch[k]!) * f;
      out[k]!.copy(qy.setFromAxisAngle(yawAxes[k]!, yaw)).multiply(qp.setFromAxisAngle(pitchAxes[k]!, pitch));
    }
  };
  return {
    get settings() {
      return settings;
    },
    get dynamics() {
      return dynamics;
    },
    get reflex() {
      return settings.reflex;
    },
    hinge,
    update(next, seconds) {
      if (next.mood !== settings.mood) {
        from = settings.mood;
        since = seconds;
      }
      settings = next;
      dynamics = { ...TAIL_DYNAMICS_DEFAULTS, ...next.dynamics, inertia: next.inertia };
    },
    offsets,
    sample(seconds, out, gains) {
      offsets(seconds, out);
      const f = moodFade(seconds - since);
      const b = moodGains(settings.mood, settings.muscle, gains.length);
      const a = f < 1 ? moodGains(from, settings.muscle, gains.length) : b;
      for (let k = 0; k < gains.length; k++) gains[k] = a[k]! + (b[k]! - a[k]!) * f;
    },
  };
}
